import { cn } from "~/lib/utils";

const MAX_VISIBLE = 7;
const DOT_PX = 8;
const GAP_PX = 6;
const STEP_PX = DOT_PX + GAP_PX;

function computeWindowStart(total: number, current: number): number {
  if (total <= MAX_VISIBLE) return 0;
  const halfWindow = Math.floor(MAX_VISIBLE / 2);
  return Math.min(Math.max(current - halfWindow, 0), total - MAX_VISIBLE);
}

interface ScrollingDotsProps {
  total: number;
  current: number;
  onSelect: (index: number) => void;
  activeClass?: string;
  inactiveClass?: string;
}

export function ScrollingDots({
  total,
  current,
  onSelect,
  activeClass = "bg-white",
  inactiveClass = "bg-white/50",
}: ScrollingDotsProps) {
  if (total <= 1) return null;

  const windowStart = computeWindowStart(total, current);
  const viewportWidth = Math.min(total, MAX_VISIBLE) * STEP_PX - GAP_PX;

  return (
    <div className="overflow-hidden" style={{ width: viewportWidth }} role="group" aria-label="Image pagination">
      <div
        className="flex gap-1.5 transition-transform duration-300 ease-out"
        style={{ transform: `translateX(${-windowStart * STEP_PX}px)` }}
      >
        {Array.from({ length: total }, (_, index) => (
          <button
            key={index}
            onClick={(e) => {
              e.stopPropagation();
              onSelect(index);
            }}
            className={cn("size-2 flex-shrink-0 rounded-full transition-colors duration-300", {
              [activeClass]: index === current,
              [inactiveClass]: index !== current,
            })}
            aria-label={`Go to image ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
